/**
 * Centralized toast messages for notepad (tasks & notes)
 * 
 * Uses i18n for translations with undo support for delete operations.
 * Preserves the undo functionality while standardizing messages.
 * 
 * @example
 * ```typescript
 * // Task deleted with undo
 * notepadToast.taskDeleted('My Task', () => restoreTask());
 * 
 * // Note created
 * notepadToast.noteCreated();
 * ```
 */

import { toast } from 'sonner';
import { logger } from "@/lib/logger";
import i18n from '@/app/i18n/i18n';

// Helper to get translation
const t = (key: string) => i18n.t(`toast.notepad.${key}`, { ns: 'common' });

const UNDO_DURATION = 5000;

/**
 * Notepad toast messages (tasks & notes)
 * Uses i18n for translations, with undo support for delete operations
 */
export const notepadToast = {
  // ========================================
  // TASK OPERATIONS
  // ========================================
  
  /**
   * Show success toast when task is deleted (with undo)
   * @param title - Task title for context
   * @param onUndo - Callback to restore the task
   */
  taskDeleted: (title: string, onUndo: () => void) => {
    toast.success(t('taskDeleted'), {
      description: title,
      duration: UNDO_DURATION,
      action: {
        label: t('undo'),
        onClick: onUndo,
      },
    });
  },

  /**
   * Show success toast when task is created
   */
  taskCreated: () => {
    toast.success(t('taskCreated'));
  },

  /**
   * Show success toast when task is updated
   */
  taskUpdated: () => {
    toast.success(t('taskUpdated'));
  },

  /**
   * Show error toast when task operation fails
   * @param error - Error object (logged to console)
   */
  taskError: (error?: Error) => {
    logger.error('[Notepad] Task operation failed:', error);
    toast.error(t('taskFailed'), {
      description: t('taskFailedDescription'),
    });
  },

  // ========================================
  // NOTE OPERATIONS
  // ========================================
  
  /**
   * Show success toast when note is deleted (with undo)
   * @param title - Note title for context
   * @param onUndo - Callback to restore the note
   */
  noteDeleted: (title: string, onUndo: () => void) => {
    toast.success(t('noteDeleted'), {
      description: title,
      duration: UNDO_DURATION,
      action: {
        label: t('undo'),
        onClick: onUndo,
      },
    });
  },

  /**
   * Show success toast when note is created
   */
  noteCreated: () => {
    toast.success(t('noteCreated'));
  },

  /**
   * Show success toast when note is updated
   */
  noteUpdated: () => {
    toast.success(t('noteUpdated'));
  },

  /**
   * Show success toast when note is pinned
   */
  notePinned: () => {
    toast.success(t('notePinned'));
  },

  /**
   * Show success toast when note is unpinned
   */
  noteUnpinned: () => {
    toast.success(t('noteUnpinned'));
  },

  /**
   * Show success toast when note is archived
   */
  noteArchived: () => {
    toast.success(t('noteArchived'));
  },

  /**
   * Show success toast when note is unarchived
   */
  noteUnarchived: () => {
    toast.success(t('noteRestored'));
  },

  /**
   * Show error toast when note operation fails
   * @param error - Error object (logged to console)
   */
  noteError: (error?: Error) => {
    logger.error('[Notepad] Note operation failed:', error);
    toast.error(t('noteFailed'), {
      description: t('noteFailedDescription'),
    });
  },

  // ========================================
  // REMINDER OPERATIONS
  // ========================================
  
  /**
   * Show success toast when reminder is set
   */
  reminderSet: () => {
    toast.success(t('reminderSet'));
  },

  /**
   * Show success toast when reminder is removed
   */
  reminderRemoved: () => {
    toast.success(t('reminderRemoved'));
  },

  /**
   * Show error toast when reminder operation fails
   * @param error - Error object (logged to console)
   */
  reminderError: (error?: Error) => {
    logger.error('[Notepad] Reminder operation failed:', error);
    toast.error(t('reminderFailed'), {
      description: t('reminderFailedDescription'),
    });
  },
};
